import React from 'react';
import { Link } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { useStore } from '../contexts/StoreContext';
import { Category } from '../types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggleCart: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, onToggleCart }) => {
  const { t, appData: localizedAppData } = useLanguage();
  const { getTotalItems } = useStore();

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Logo */}
          <div className="p-4 border-b border-gray-200">
            <Link to="/" onClick={onClose}>
              <img
                src="https://www.cleverdetails.com/images/logo.png"
                alt="Clever Details"
                className="h-16 w-auto"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/logo.png";
                }}
              />
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 overflow-y-auto">
            {/* Categories Section */}
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primary mb-4">{t.sidebar.categories}</h2>
              <nav className="space-y-2">
                {localizedAppData.categories.map((category: Category) => (
                  <Link
                    key={category.id}
                    to={category.route}
                    onClick={onClose}
                    className="block text-gray-600 hover:text-secondary transition-colors"
                  >
                    {category.title}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Other Links Section */}
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primary mb-4">{t.sidebar.other}</h2>
              <nav className="space-y-2">
                <Link
                  to="/all"
                  onClick={onClose}
                  className="block text-gray-600 hover:text-secondary transition-colors"
                >
                  {t.header.menu.advantages}
                </Link>
                <Link
                  to="/shopping"
                  onClick={onClose}
                  className="block text-gray-600 hover:text-secondary transition-colors"
                >
                  {t.shopping.title}
                </Link>
                <Link
                  to="/access"
                  onClick={onClose}
                  className="block text-gray-600 hover:text-secondary transition-colors"
                >
                  {t.header.menu.access}
                </Link>
                <Link
                  to="/partnership"
                  onClick={onClose}
                  className="block text-gray-600 hover:text-secondary transition-colors"
                >
                  {t.header.menu.partnership}
                </Link>
                <Link
                  to="/about"
                  onClick={onClose}
                  className="block text-gray-600 hover:text-secondary transition-colors"
                >
                  {t.header.menu.about}
                </Link>
                <Link
                  to="/contact"
                  onClick={onClose}
                  className="block text-gray-600 hover:text-secondary transition-colors"
                >
                  {t.header.menu.contact}
                </Link>
              </nav>
            </div>

            {/* cart button */}
            <div className="p-4 border-t border-gray-200">
              {/* Cart Button */}
              <button
                onClick={() => {
                  onToggleCart();
                  onClose();
                }}
                className="flex items-center gap-2 text-secondary transition-colors p-2 relative ml-4 hover:text-orange-400"
                aria-label="Toggle Cart"
              >
                <svg 
                  className="w-6 h-6" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" clipRule="evenodd" d="M3.25 4C3.25 3.58579 3.58579 3.25 4 3.25H5.14208C6.00583 3.25 6.74039 3.88019 6.87173 4.7339L7.11776 6.33306C7.28591 6.2792 7.46564 6.25 7.65301 6.25H17.347C18.412 6.25 19.23 7.19323 19.0794 8.24749L18.6104 11.5303C18.5185 12.1738 18.2666 12.7597 17.8985 13.25H18C19.2426 13.25 20.25 14.2574 20.25 15.5C20.25 16.5376 19.5476 17.4112 18.5924 17.6712C18.6941 17.9277 18.75 18.2073 18.75 18.5C18.75 19.7426 17.7426 20.75 16.5 20.75C15.2574 20.75 14.25 19.7426 14.25 18.5C14.25 18.237 14.2951 17.9846 14.378 17.75H10.622C10.7049 17.9846 10.75 18.237 10.75 18.5C10.75 19.7426 9.74264 20.75 8.5 20.75C7.25736 20.75 6.25 19.7426 6.25 18.5C6.25 17.2574 7.25736 16.25 8.5 16.25H18C18.4142 16.25 18.75 15.9142 18.75 15.5C18.75 15.0858 18.4142 14.75 18 14.75H8.3673C7.49641 14.75 6.75805 14.1096 6.63488 13.2475L6.25811 10.6101L5.38917 4.96199C5.37041 4.84003 5.26547 4.75 5.14208 4.75H4C3.58579 4.75 3.25 4.41421 3.25 4ZM7.74266 10.3953L8.11981 13.0354C8.1374 13.1585 8.24288 13.25 8.3673 13.25H14.8981C16.0178 13.25 16.9671 12.4267 17.1255 11.3182L17.5945 8.03535C17.616 7.88475 17.4991 7.75 17.347 7.75H7.65301C7.50087 7.75 7.38401 7.88475 7.40552 8.03536L7.74231 10.3929C7.74243 10.3937 7.74254 10.3945 7.74266 10.3953ZM16.5 17.75C16.0858 17.75 15.75 18.0858 15.75 18.5C15.75 18.9142 16.0858 19.25 16.5 19.25C16.9142 19.25 17.25 18.9142 17.25 18.5C17.25 18.0858 16.9142 17.75 16.5 17.75ZM8.5 17.75C8.08579 17.75 7.75 18.0858 7.75 18.5C7.75 18.9142 8.08579 19.25 8.5 19.25C8.91421 19.25 9.25 18.9142 9.25 18.5C9.25 18.0858 8.91421 17.75 8.5 17.75Z"/>
                </svg>
                <span className="font-semibold">{t.shopping.cart}: {getTotalItems} items</span>
                {getTotalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-secondary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold z-[9999] hover:scale-110 transition-all duration-200" style={{ right: '-8px', top: '2px' }}>
                    {getTotalItems}
                  </span>
                )}
              </button>
            </div>
            

            {/* Language Selector */}
            <div className="p-4 border-t border-gray-200">
              <h2 className="text-xl font-bold text-primary mb-4">{t.sidebar.language}</h2>
              <LanguageSwitcher variant="sidebar" onClose={onClose} />
            </div>

            {/* add "we love vilamoura" text, make it go to bottom of the sidebar */}
            <div className="absolute bottom-0 left-0 right-0 p-4 hidden md:block">
              <span className="text-secondary font-bold text-xl block" style={{ fontFamily: "'Tropika Script', cursive" }}>
                We ❤️ Vilamoura ...
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;